import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Eye, Loader2, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import LeadershipPageHeader from '@/components/LeadershipPageHeader';
import { LEADERSHIP_CONTENT } from '@/config/leadershipLayout';
import ReferralStatusBadge from '../components/ReferralStatusBadge';
import ReferralApprovalDetailDrawer from '../components/ReferralApprovalDetailDrawer';
import { referralApprovalApi } from '../api/referralApproval.api';

const STAGE_LABEL = {
  review_1: 'Review 1',
  review_2: 'Review 2',
  approval: 'Final approval',
};

/** Inbox for FIC / Dean / Director — act on assigned approval stage. */
export default function ReferralApprovalInboxPage() {
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [viewing, setViewing] = useState(null);
  const [intent, setIntent] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    referralApprovalApi.inbox()
      .then((data) => setItems(data?.items || []))
      .catch((e) => {
        toast.error(e?.response?.data?.detail || 'Could not load inbox');
        setItems([]);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  // Deep-link from approver email: ?open=<id>&intent=approve|reject
  useEffect(() => {
    const openId = (params.get('open') || '').trim();
    if (!openId || loading) return undefined;
    let cancelled = false;
    const fromList = items.find((r) => r.id === openId);
    const openRow = async () => {
      try {
        const row = fromList || await referralApprovalApi.inboxOne(openId);
        if (cancelled || !row) return;
        setViewing(row);
        const i = (params.get('intent') || '').toLowerCase();
        setIntent(i === 'approve' || i === 'reject' ? i : null);
        setParams({}, { replace: true });
      } catch (e) {
        if (!cancelled) {
          toast.error(e?.response?.data?.detail || 'Could not open referral from email link');
        }
      }
    };
    openRow();
    return () => { cancelled = true; };
  }, [params, items, loading, setParams]);

  const decide = async (id, decision) => {
    setBusy(true);
    try {
      await referralApprovalApi.decide(id, { decision });
      toast.success(decision === 'approved' ? 'Approved — next stage or doctor queue' : 'Rejected');
      setViewing(null);
      setIntent(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`${LEADERSHIP_CONTENT} space-y-5`} data-testid="referral-approval-inbox">
      <LeadershipPageHeader
        title="Referral approval inbox"
        subtitle="Out-station requests waiting on your stage (Review 1, Review 2, or Approval)."
      />
      {loading ? (
        <div className="py-16 text-center"><Loader2 className="h-7 w-7 animate-spin mx-auto text-[#F15A22]" /></div>
      ) : !items.length ? (
        <p className="text-sm text-[#7A8B9C] rounded-2xl border border-dashed border-[#D5DEE8] bg-white px-4 py-10 text-center">
          No referrals awaiting your approval.
        </p>
      ) : (
        <ul className="space-y-3">
          {items.map((r) => (
            <li key={r.id} className="rounded-2xl border border-[#D5DEE8] bg-white p-4 shadow-sm">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="font-semibold text-[#071B34]">{r.request_number}</p>
                  <p className="text-sm text-[#5A6B7C] mt-0.5">
                    {r.patient?.name} · {r.seeking?.specialisation}
                    {r.preferred_doctor_name ? ` → Dr ${r.preferred_doctor_name}` : ''}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <ReferralStatusBadge status={r.status} />
                    <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-0.5 text-[11px] font-semibold text-amber-900">
                      {STAGE_LABEL[r.fic_stage] || r.fic_stage || 'Review'}
                    </span>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => { setIntent(null); setViewing(r); }}>
                    <Eye className="h-3.5 w-3.5 mr-1" /> View form
                  </Button>
                  <Button size="sm" variant="outline" disabled={busy} onClick={() => decide(r.id, 'rejected')}>
                    <X className="h-3.5 w-3.5 mr-1" /> Reject
                  </Button>
                  <Button
                    size="sm"
                    className="text-white"
                    style={{ background: '#F15A22' }}
                    disabled={busy}
                    onClick={() => decide(r.id, 'approved')}
                  >
                    <Check className="h-3.5 w-3.5 mr-1" /> Approve
                  </Button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      <ReferralApprovalDetailDrawer
        open={Boolean(viewing)}
        row={viewing}
        busy={busy}
        intent={intent}
        onClose={() => { setViewing(null); setIntent(null); }}
        onDecide={decide}
      />
    </div>
  );
}
